import MarketStatsBar from 'src/components/MarketStatsBar/MarketStatsBar';
import { MarketSelectorDisplay } from 'src/components/MarketSelector/MarketSelector';
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import MobileTradeFormAndOrderbook from './MobileTradeFormAndOrderbook';
import TradePageTable from 'src/components/TradePageTable/TradePageTable';
import MobileChartAndOrderbook from 'src/components/MobileChartAndOrderbook';
import { cj } from 'src/utils/classNames';
import { useWindowSizeStore } from 'src/stores/useWindowSizeStore';
import TradePageMobileBottomBar from 'src/components/TradePageGrid/mobile/TradePageMobileBottomBar';
import { MobileBottomToggleTab } from 'src/components/TradePageGrid/mobile/types';
import HiddenMarketBanner from 'src/components/StatusBars/HiddenMarketBanner';

const DEFAULT_HEIGHTS = { mainContainer: 0, bottomTabBar: 0 };

export default function MobileMainPage() {
	const windowHeight = useWindowSizeStore((s) => s.realtimeHeight);
	const [heights, setHeights] = useState(DEFAULT_HEIGHTS);
	const [selectedTab, setSelectedTab] =
		useState<MobileBottomToggleTab>('trade');

	const marketBarRef = useRef<HTMLDivElement>(null);
	const fundingRateBarRef = useRef<HTMLDivElement>(null);
	const bottomTabBarRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const marketBarHeight = marketBarRef.current?.clientHeight ?? 0;
		const fundingRateBarHeight = fundingRateBarRef.current?.clientHeight ?? 0;
		const headerHeight = document.querySelector('.header')?.clientHeight ?? 0;
		const tableTabsHeight =
			document.querySelector('#trade_page_table_tabs')?.clientHeight ?? 0;
		const bottomTabBarHeight = bottomTabBarRef.current?.clientHeight ?? 0;

		const mainContainerHeight =
			windowHeight -
			marketBarHeight -
			fundingRateBarHeight -
			headerHeight -
			tableTabsHeight -
			bottomTabBarHeight;

		setHeights({
			mainContainer: mainContainerHeight,
			bottomTabBar: bottomTabBarHeight,
		});
		// `selectedTab` isn't used in here but it's important to include it as a dep.
		// There are different elements on the page depending on the tab, so we need
		// to recalculate the height accordingly.
	}, [windowHeight, selectedTab]);

	const chartAndOrderbookStyles = useMemo(() => {
		return {
			height: heights.mainContainer,
			maxHeight: heights.mainContainer,
		};
	}, [heights.mainContainer]);

	const tableStyles = useMemo(() => {
		return {
			paddingBottom: heights.bottomTabBar,
		};
	}, [heights.bottomTabBar]);

	return (
		// Wrapper `div` necessary for inner elements to maintain correct heights.
		<div>
			<div className="h-12 bg-container-bg" ref={marketBarRef}>
				<MarketSelectorDisplay />
			</div>

			{selectedTab === 'chart' && (
				<div className="z-40 pb-xs" ref={fundingRateBarRef}>
					<MarketStatsBar />
				</div>
			)}

			<HiddenMarketBanner
				className={cj(selectedTab === 'chart' ? 'mb-1' : 'mt-1')}
			/>

			{/** Main Content - `hidden` so we don't have to re-render the components when switching tabs */}
			<div className={cj(selectedTab !== 'trade' && 'hidden')}>
				<MobileTradeFormAndOrderbook />
			</div>
			<div
				className={cj(
					'mb-xs overflow-hidden',
					selectedTab !== 'chart' && 'hidden'
				)}
				style={chartAndOrderbookStyles}
			>
				<MobileChartAndOrderbook />
			</div>

			<div style={tableStyles}>
				<TradePageTable className="h-auto" />
			</div>

			<TradePageMobileBottomBar
				selectedTab={selectedTab}
				onTabChange={setSelectedTab}
				ref={bottomTabBarRef}
			/>
		</div>
	);
}
